"use client";

import type { useChat } from "@ai-sdk/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import { graphql } from "gql.tada";
import { Skeleton } from "@/components/ui/skeleton";
import type { AgentUIMessage } from "@/lib/ai/types";
import { ApproveButton } from "../ui/approve-button";
import { DenyButton } from "../ui/deny-button";
import { CREATE_SA_BIGQUERY_NAME } from "./constants";
import type { CreateSearchAnalyticsBigQueryInput } from "./schema";

const ProjectDataQuery = graphql(`
  query SaBigQueryConfirmProject($id: ID!) {
    node(id: $id) {
      __typename
      ... on SearchAnalyticsProject {
        name
        searchAnalytics {
          id
          __typename
        }
      }
    }
  }
`);

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

export function CreateSearchAnalyticsBigQueryConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props) {
  const isTarget =
    isToolUIPart(part) && getToolName(part) === CREATE_SA_BIGQUERY_NAME;
  const isInputAvailable = part.state === "input-available";

  const input = (part.input ??
    {}) as Partial<CreateSearchAnalyticsBigQueryInput>;
  const projectId = input.projectId ?? "";
  const manageType = input.manageType ?? "SELF";
  const isApi = manageType === "API";

  const [{ data, fetching }] = useQuery({
    query: ProjectDataQuery,
    variables: { id: projectId },
    pause: !(isTarget && isInputAvailable && projectId),
  });

  const projectName =
    data?.node?.__typename === "SearchAnalyticsProject"
      ? data.node.name
      : undefined;

  if (!isTarget || !isInputAvailable) return null;

  return (
    <div className="p-4 text-sm">
      <div className="mb-2 font-medium">
        <Trans>Register Search Analytics BigQuery configuration?</Trans>
      </div>
      <ul className="mb-4 list-disc space-y-1 pl-5">
        <li>
          <Trans>Manage type</Trans>:{" "}
          <span className="font-medium">{manageType}</span>
        </li>
        {isApi ? (
          <>
            <li>
              <Trans>Search Console property</Trans>:{" "}
              <span className="break-all font-mono font-medium">
                {input.propertyID}
              </span>
            </li>
            <li>
              <Trans>
                Backfill history: {input.backfillMonths ?? 16} months
              </Trans>
            </li>
            <li className="text-muted-foreground">
              <Trans>
                API connection limitations: Search Appearances is unavailable,
                and capture rate may be below 100%.
              </Trans>
            </li>
          </>
        ) : (
          <>
            <li>
              <Trans>GCP project</Trans>:{" "}
              <span className="font-mono font-medium">{input.projectID}</span>
            </li>
            <li>
              <Trans>Dataset</Trans>:{" "}
              <span className="font-mono font-medium">{input.datasetName}</span>
            </li>
          </>
        )}
        {input.organizationId ? (
          <li>
            <Trans>Organization ID</Trans>:{" "}
            <span className="break-all font-mono text-xs">
              {input.organizationId}
            </span>
          </li>
        ) : null}
        {projectId ? (
          <li>
            <Trans>Project</Trans>:{" "}
            {fetching ? (
              <Skeleton className="h-4 w-12" />
            ) : (
              <span className="font-medium">{projectName ?? "Unknown"}</span>
            )}
          </li>
        ) : null}
      </ul>
      <div className="flex gap-2">
        <ApproveButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Register</Trans>
        </ApproveButton>
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    </div>
  );
}
